import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideMail } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { LogoComponent } from '../../components/logo/logo.component';
import { PartnerService } from '../../services/partner.service';
import { AffiliateSignupFormComponent } from './components/affiliate-signup-form/affiliate-signup-form.component';

/**
 * Public partner page (/partner): sign up like a shop, with e-mail and password (then
 * confirm the address) or Google (straight into the partner area, which asks for the
 * name next). A logged-in partner goes straight in.
 */
@Component({
  selector: 'app-affiliate',
  standalone: true,
  imports: [
    RouterModule,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmCardImports,
    HlmSpinnerImports,
    LogoComponent,
    AffiliateSignupFormComponent,
  ],
  providers: [provideIcons({ lucideMail })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './affiliate.component.html',
})
export class AffiliateComponent {
  private readonly partnerService = inject(PartnerService);
  private readonly router = inject(Router);

  /** Set after a password sign-up: the address waiting for confirmation. */
  readonly pendingEmail = signal<string | null>(null);
  readonly resending = signal(false);
  readonly resent = signal(false);

  constructor() {
    // Old mails link "/partner?link=resend": the login page mails links now
    const oldResendLink = inject(ActivatedRoute).snapshot.queryParamMap.get('link') === 'resend';
    if (this.partnerService.isLoggedIn()) {
      this.router.navigate(['/partner/clients']);
    } else if (oldResendLink) {
      this.router.navigate(['/partner/login']);
    }
  }

  onSignedIn(): void {
    this.router.navigate(['/partner/clients']);
  }

  resend(): void {
    const email = this.pendingEmail();
    if (!email || this.resending()) return;
    this.resending.set(true);
    this.partnerService.resendVerification(email).subscribe({
      next: () => {
        this.resending.set(false);
        this.resent.set(true);
      },
      error: () => this.resending.set(false),
    });
  }
}
